import React, { FC, Dispatch, useState, useEffect, useContext } from 'react';

import { Button, Checkbox, Icon, Modal } from 'semantic-ui-react';

import CartContext, { CartDishType, CombinationType } from 'contexts/CartContext';

import {
  ActionType,
  VendorActionType,
  AddDishActionType,
  RemoveDishActionType,
} from 'reducers/CartReducer';

import theme from './theme.scss';

type ExtraType = {
  id: string;
  name: string;
  price: number;
  isVegetarian: boolean;
};

export interface DishType {
  id: string;
  name: string;
  description: string;
  image: string;
  price: number;
  isVegetarian: boolean;
  category: {
    id: string;
    name: string;
  };
  addOns: ExtraType[];
  options: ExtraType[];
  notes: string;
  scheduledTime: string;
}

type AddDishProps = {
  dish: DishType | CartDishType;
  addOns?: ExtraType[];
  options?: ExtraType[];
  vendor?: {
    id: string;
    name: string;
  };
};

const CreateCombinationModal: FC<{
  open: boolean;
  onSubmit: (combination: CombinationType) => void;
  onClose: () => void;
  addOns?: ExtraType[];
  options?: ExtraType[];
}> = ({ open, onSubmit, onClose, addOns = [], options = [] }) => {
  const [selectedAddOns, setSelectedAddOns] = useState<any[]>([]);
  const [selectedOption, setSelectedOption] = useState<any>({
    id: '',
  });

  function setDefaultOption() {
    if (options) {
      setSelectedOption(options[0]);
    }
  }

  useEffect(() => {
    setDefaultOption();
  }, []);

  return (
    <Modal animationType="none" transparent={true} visible={open} onRequestClose={onClose}>
      <div>
        <p>Options</p>
        {options.map(option => (
          <Checkbox
            key={option.id}
            center
            title={`${option.name} (+ ₹${option.price})`}
            checkedIcon="dot-circle-o"
            uncheckedIcon="circle-o"
            checked={selectedOption.id === option.id}
            containerStyle={{
              borderWidth: 0,
              borderRadius: 0,
              borderLeftWidth: 3,
              borderColor: option.isVegetarian ? '#4CAF50' : '#e53935',
              backgroundColor: '#fff',
            }}
            textStyle={{
              textAlign: 'left',
            }}
            onPress={() => setSelectedOption(option)}
          />
        ))}
        <p>Add Ons</p>
        {addOns.map(addOn => (
          <Checkbox
            key={addOn.id}
            center
            title={`${addOn.name} (+ ₹ ${addOn.price})`}
            checked={selectedAddOns.includes(addOn)}
            containerStyle={{
              borderWidth: 0,
              borderRadius: 0,
              borderLeftWidth: 3,
              borderColor: addOn.isVegetarian ? '#4CAF50' : '#e53935',
              backgroundColor: '#fff',
            }}
            textStyle={{
              textAlign: 'left',
            }}
            onPress={() => {
              if (selectedAddOns.includes(addOn)) {
                setSelectedAddOns(selectedAddOns.filter(a => a !== addOn));
              } else {
                setSelectedAddOns([...selectedAddOns, addOn]);
              }
            }}
          />
        ))}
        <div>
          <Button
            title="Add to Cart"
            onPress={() => {
              onSubmit({
                option: selectedOption,
                addOns: selectedAddOns,
              });
              setSelectedAddOns([]);
              setDefaultOption();
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

const RemoveCombinationModal: FC<{
  currentCombinations: CombinationType[];
  open: boolean;
  onSubmit: (modifiedCombinations: CombinationType[]) => void;
  onClose: () => void;
}> = ({ open, currentCombinations, onSubmit, onClose }) => {
  const [selectedOption, setSelectedOption] = useState<number>(0);

  const getTitle = (combination: CombinationType) => {
    const addOns =
      combination.addOns && combination.addOns.length
        ? `- (${combination.addOns.map(a => a.name).join(', ')})`
        : '';
    return `${combination.option.name} ${addOns}`;
  };

  return (
    <Modal animationType="none" transparent={true} visible={open} onRequestClose={onClose}>
      <div>
        <p>Chosen combinations</p>
        {currentCombinations.map((combination, i) => (
          <Checkbox
            key={`current-combination-${i}`}
            center
            title={getTitle(combination)}
            checkedIcon="dot-circle-o"
            uncheckedIcon="circle-o"
            checked={selectedOption === i}
            containerStyle={{
              borderWidth: 0,
              backgroundColor: '#fff',
            }}
            textStyle={{
              textAlign: 'left',
            }}
            onPress={() => setSelectedOption(i)}
          />
        ))}
        <div>
          <Button
            title="Remove from Cart"
            onClick={() => onSubmit(currentCombinations.filter((_c, i) => i !== selectedOption))}
          />
        </div>
      </div>
    </Modal>
  );
};

const AddDishButton: FC<AddDishProps> = ({ dish, addOns, options, vendor }) => {
  const { cartState, cartDispatch } = useContext(CartContext);

  const vendorDispatch: Dispatch<ActionType & VendorActionType> = cartDispatch;
  const addDishDispatch: Dispatch<ActionType & AddDishActionType> = cartDispatch;
  const removeDishDispatch: Dispatch<ActionType & RemoveDishActionType> = cartDispatch;

  const dishFound = cartState.dishesOnCart.find(d => d.id === dish.id);

  let currentCount = 0;
  let currentCombinations: CombinationType[] = [];

  if (dishFound) {
    currentCount = dishFound.quantity;
    currentCombinations = dishFound.combinations ? [...dishFound.combinations] : [];
  }

  const [showCreateCombinationModal, toggleCreateCombinationModal] = useState(false);
  const [showRemoveCombinationModal, toggleRemoveCombinationModal] = useState(false);

  const incrementDishCount = () => {
    if ((addOns && addOns.length) || (options && options.length)) {
      toggleCreateCombinationModal(true);
      return;
    }
    checkVendorAndAddDish(dish);
  };

  const decrementDishCount = () => {
    if (currentCombinations && currentCombinations.length) {
      toggleRemoveCombinationModal(true);
      return;
    }
    removeDish(dish);
  };

  const checkVendorAndAddDish = (dish: DishType | CartDishType, combination?: CombinationType) => {
    let totalQuantity = 0;
    cartState.dishesOnCart.forEach(dish => {
      totalQuantity += dish.quantity;
    });
    if (totalQuantity > 14) {
      window.alert(
        'Cart Limit Reached: This restaurant has a limit for how many items can be ordered at once.'
      );
      return;
    }

    if (cartState.vendorOnCart.id && cartState.dishesOnCart.length) {
      if (vendor && vendor.id !== cartState.vendorOnCart.id) {
        // const message = `Your cart contains dishes from ${cartState.vendorOnCart.name}. Do you want to remove those dishes and order from here instead?`;
        if (window.confirm('Replace cart dish?')) {
          vendorDispatch({
            type: 'VENDOR',
            payload: {
              id: vendor.id,
              name: vendor.name,
            },
          });
          addDish(dish, combination);
        }
      } else {
        addDish(dish, combination);
      }
    } else {
      if (vendor) {
        vendorDispatch({
          type: 'VENDOR',
          payload: {
            id: vendor.id,
            name: vendor.name,
          },
        });
      }
      addDish(dish, combination);
    }
  };

  const addDish = (dish: DishType | CartDishType, combination?: CombinationType) => {
    addDishDispatch({
      type: 'ADD_DISH',
      payload: {
        dish,
        combination,
      },
    });
  };

  const removeDish = (dish: DishType | CartDishType, modifiedCombinations?: CombinationType[]) => {
    removeDishDispatch({
      type: 'REMOVE_DISH',
      payload: {
        dish,
        modifiedCombinations,
      },
    });
  };

  return (
    <div>
      {currentCount === 0 ? (
        <Button icon onClick={incrementDishCount}>
          <Icon name="add" />
        </Button>
      ) : (
        <div>
          <Button icon onClick={decrementDishCount}>
            <Icon name="minus" />
          </Button>
          <span className={theme.count}>{currentCount}</span>
          <Button icon onClick={incrementDishCount}>
            <Icon name="add" />
          </Button>
        </div>
      )}
      <CreateCombinationModal
        open={showCreateCombinationModal}
        addOns={addOns}
        options={options}
        onSubmit={combination => {
          checkVendorAndAddDish(dish, combination);
          toggleCreateCombinationModal(false);
        }}
        onClose={() => {
          toggleCreateCombinationModal(false);
        }}
      />
      <RemoveCombinationModal
        open={showRemoveCombinationModal}
        currentCombinations={currentCombinations}
        onSubmit={modifiedCombinations => {
          removeDish(dish, modifiedCombinations);
          toggleRemoveCombinationModal(false);
        }}
        onClose={() => {
          toggleRemoveCombinationModal(false);
        }}
      />
    </div>
  );
};

export default AddDishButton;
